import { queryOptions, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  appointmentBandOf,
  getPerson,
  holdsASeat,
  listTeamCandidates,
  listTeamPlayers,
  publishTeamSheet,
  putTeamSheet,
  readTeamSheet,
  type CallUpRead,
  type MatchKitPut,
  type MatchRead,
  type MatchSide,
  type Me,
  type PersonRead,
  type TeamBrief,
  type TeamCandidateRead,
  type TeamRosterPlayerRead,
  type TeamSheetPut,
  type TeamSheetRead,
} from '@volee/api-client'
import type { AvatarFace } from '@volee/ui'
import * as React from 'react'

import { faceOf, heldSquad, squadLabel } from './labels.ts'
import { hasStarted } from './match-kickoff.ts'
import { meetingInstant, meetingTimeOf } from './meeting-time.ts'
import { fromMatchKit, toMatchKitPut, type MatchKitDraft } from './match-kit.ts'
import { teamMatchesQuery } from './matches.ts'
import { api } from '../services/api.ts'
import { interpolate, messages, type PlayerPosition } from '../messages/index.ts'

// R14-R18 · the call-up screen's data layer: the viewer's own side of the match, the
// club's tesserati, the draft the three steps share, and the two writes it ends in.

export const MAX_CALLED_UP = 20
/** What the API takes of the notes for the families. */
export const MAX_NOTES = 280

/** Which side of the match the viewer's own team plays, mirroring `require_team_official`: by the incarico's
 *  band, since a null `team_id` may be a coach still waiting for a squad, and by the seat on that squad. */
export function sideForViewer(match: MatchRead, me: Me): MatchSide | null {
  function belongsToViewer(side: MatchSide): boolean {
    if (me.staff_role !== null) return true
    return me.appointments.some((held) => {
      if (held.club_id !== side.club_id) return false
      const band = appointmentBandOf(held.role, held.team_id)
      return (
        band === 'club' || (band === 'team' && holdsASeat(held) && held.team_id === side.team_id)
      )
    })
  }

  if (belongsToViewer(match.home)) return match.home
  if (belongsToViewer(match.away)) return match.away
  return null
}

/** After the whistle (the server's own, `hasStarted`) a published call-up keeps who is called and changes the
 *  numbers alone, and the page is those numbers. One never published, a backlog one, is still written whole. */
export function isNumbersOnly(
  kicksOffAt: string,
  sheet: Pick<TeamSheetRead, 'published_at'>,
  now: Date = new Date(),
): boolean {
  return sheet.published_at !== null && hasStarted(kicksOffAt, now)
}

export function lineupCandidatesKey(teamId: number) {
  return ['lineup', 'candidates', teamId] as const
}

export interface LineupPerson {
  readonly registrationId: number
  readonly personId: number
  readonly lastName: string
  readonly firstName: string
  readonly position: PlayerPosition | null
  /** The circle of the row: initials, and the signed photo when the server sends one. */
  readonly face: AvatarFace
  /** In this team's own squad: the step's page lists these. Others are the club's remaining
   *  tesserati, playable as fuori-quota, and called up from a sheet of their own. */
  readonly inSquad: boolean
  /** The leva holding one outside the squad, which the sheet names and filters by: `null` for one
   *  no leva holds, and for the squad's own. */
  readonly otherSquad: TeamBrief | null
}

/** The name as the convocazione reads it out, «Cognome Nome»: the rows draw it in two lines. */
export function lineupPersonName(player: LineupPerson): string {
  return `${player.lastName} ${player.firstName}`
}

/** The name as the rows read it, «Cognome Nome», holds the search: everybody, without one. */
export function matchesName(player: LineupPerson, query: string | null): boolean {
  if (query === null) return true
  return lineupPersonName(player).toLowerCase().includes(query.toLowerCase())
}

/** The leva one outside the squad comes from, as every list names a squad, or «Senza leva». */
export function otherSquadLabel(player: LineupPerson): string {
  if (player.otherSquad === null) return messages.lineup.otherSquads.noSquad
  return squadLabel(player.otherSquad)
}

/** Who a registration is, among the candidates: `undefined` for one they do not hold, or for none. */
export function personOf(
  candidates: readonly LineupPerson[],
  registrationId: number | null | undefined,
): LineupPerson | undefined {
  return candidates.find((candidate) => candidate.registrationId === registrationId)
}

/** A rosa row and a candidate row say the same of a person: only where they stand differs. */
function toLineupPerson(
  row: TeamRosterPlayerRead | TeamCandidateRead,
  inSquad: boolean,
  otherSquad: TeamBrief | null,
): LineupPerson {
  return {
    registrationId: row.registration_id,
    personId: row.person_id,
    lastName: row.last_name,
    firstName: row.first_name,
    position: row.position ?? null,
    face: faceOf(row),
    inSquad,
    otherSquad,
  }
}

/** The squad first, then the rest of the club's tesserati — one stable list, never
 *  reordered by who is called up. Two lists merged client-side: no endpoint answers both
 *  in one shape, and `listTeamCandidates` already excludes the squad's own players. */
export function lineupCandidatesQuery(teamId: number) {
  return queryOptions({
    queryKey: lineupCandidatesKey(teamId),
    queryFn: async (): Promise<readonly LineupPerson[]> => {
      const [roster, candidates] = await Promise.all([
        listTeamPlayers(api, teamId),
        listTeamCandidates(api, teamId, { limit: 200 }),
      ])
      return [
        ...roster.items.map((row) => toLineupPerson(row, true, null)),
        ...candidates.items.map((row) => toLineupPerson(row, false, heldSquad(row) ?? null)),
      ]
    },
  })
}

export function teamSheetKey(matchId: number, enrollmentId: number) {
  return ['lineup', 'team-sheet', matchId, enrollmentId] as const
}

export function teamSheetQuery(matchId: number, enrollmentId: number) {
  return queryOptions({
    queryKey: teamSheetKey(matchId, enrollmentId),
    queryFn: () => readTeamSheet(api, matchId, enrollmentId),
  })
}

/** The FIGC number lives on `PersonRead`, not on the roster rows — step 3 asks for it only
 *  for the players actually called up, capped at `MAX_CALLED_UP`, so this is bounded. */
export function useMissingFigcNumbers(personIds: readonly number[]): ReadonlySet<number> {
  const results = useQueries({
    queries: personIds.map((personId) => ({
      queryKey: ['lineup', 'person', personId] as const,
      queryFn: (): Promise<PersonRead> => getPerson(api, personId),
      staleTime: 5 * 60 * 1000,
    })),
  })
  const missing = new Set<number>()
  results.forEach((result, index) => {
    const personId = personIds[index]
    if (personId !== undefined && result.data !== undefined && result.data.figc_number == null) {
      missing.add(personId)
    }
  })
  return missing
}

export interface CallUpEntry {
  readonly registrationId: number
  /** The coach may not know it: only a numbered call-up is sent to the analysis. */
  readonly shirtNumber: number | null
}

export interface LineupDraftState {
  readonly entries: readonly CallUpEntry[]
  /** La divisa di gara. `null` is «non indicata», and only a confirmation fills it. */
  readonly matchKit: MatchKitDraft | null
  /** Il ritrovo as typed in its field: `HH:MM`, or the empty string for «non indicato». */
  readonly meetingTime: string
  /** Where, when it is not the pitch; the empty string says «al campo». */
  readonly meetingPlace: string
  /** For the families, at most `MAX_NOTES` characters. */
  readonly notes: string
}

/** `called-up-full`: the twentieth is the last one the API takes. */
export type ToggleOutcome = 'ok' | 'called-up-full'

function withoutEntry(entries: readonly CallUpEntry[], registrationId: number): CallUpEntry[] {
  return entries.filter((entry) => entry.registrationId !== registrationId)
}

function emptyState(): LineupDraftState {
  return {
    entries: [],
    matchKit: null,
    meetingTime: '',
    meetingPlace: '',
    notes: '',
  }
}

function toCallUpEntry(callUp: CallUpRead): CallUpEntry {
  return { registrationId: callUp.player_registration_id, shirtNumber: callUp.shirt_number }
}

function fromTeamSheet(sheet: TeamSheetRead): LineupDraftState {
  return {
    entries: sheet.call_ups.map(toCallUpEntry),
    matchKit: fromMatchKit(sheet.match_kit),
    meetingTime: meetingTimeOf(sheet.meeting_at),
    meetingPlace: sheet.meeting_place ?? '',
    notes: sheet.notes ?? '',
  }
}

export type LineupDraft = ReturnType<typeof useLineupDraft>

/** The colours of other colours as the server files them, in capitals: a save comes back with «#1F3A93» for the
 *  «#1f3a93» the picker gave, and that is the same kit, not a change left to save. */
function inCapitals(put: MatchKitPut | null): MatchKitPut | null {
  if (put === null || put.choice !== 'other') return put
  const { secondary_color: secondary, ...rest } = put
  return {
    ...rest,
    primary_color: put.primary_color.toUpperCase(),
    shorts_color: put.shorts_color.toUpperCase(),
    ...(secondary == null ? {} : { secondary_color: secondary.toUpperCase() }),
  }
}

/** Everything a save would write, in one comparable string: two drafts with the same changes read
 *  the same, whatever order the rows were called up in, however the notes were padded and in whatever case
 *  the colours were typed. */
function changesOf(state: LineupDraftState): string {
  const entries = [...state.entries].sort((a, b) => a.registrationId - b.registrationId)
  return JSON.stringify({
    entries,
    matchKit: inCapitals(toMatchKitPut(state.matchKit)),
    meetingTime: state.meetingTime,
    meetingPlace: orNull(state.meetingPlace),
    notes: orNull(state.notes),
  })
}

/** Nothing in the draft that the sheet it was seeded from does not say: one not yet seeded, or equal to its seed. */
function isUntouched(state: LineupDraftState, seed: TeamSheetRead | undefined): boolean {
  return seed === undefined || changesOf(state) === changesOf(fromTeamSheet(seed))
}

/** The editing surface for the call-up, seeded from the sheet on the server. A fresher sheet (a family's answer
 *  rides on the same read) reseeds it only while nothing in it is unsaved: typing survives a reply. */
export function useLineupDraft(sheet: TeamSheetRead | undefined) {
  const [state, setState] = React.useState<LineupDraftState>(emptyState)
  const [seededFrom, setSeededFrom] = React.useState<TeamSheetRead | undefined>(undefined)

  if (sheet !== undefined && sheet !== seededFrom) {
    setSeededFrom(sheet)
    if (isUntouched(state, seededFrom)) setState(fromTeamSheet(sheet))
  }

  const calledUp = state.entries
  const takenNumbers = new Set(
    calledUp.flatMap((entry) => (entry.shirtNumber === null ? [] : [entry.shirtNumber])),
  )

  function isCalledUp(registrationId: number): boolean {
    return calledUp.some((entry) => entry.registrationId === registrationId)
  }

  return {
    state,
    /** What the browser holds differs from the convocazione read: saving would change it. */
    dirty: !isUntouched(state, seededFrom),
    calledUp,
    /** Called up and with no number: they will have no statistics and no highlights. */
    unnumbered: calledUp.filter((entry) => entry.shirtNumber === null),
    /** Two called up wear the same number: the API refuses a convocazione like that, so the flow does not go on. */
    sharedNumber: hasSharedNumber(calledUp),
    /** A place with no time: the API refuses it too, so the summary does not publish it. */
    placeWithoutTime: isPlaceWithoutTime(state),
    takenNumbers,
    isCalledUp,
    /** The kit the server holds for this side, whose copy it keeps when chosen again. */
    savedMatchKit: seededFrom === undefined ? null : fromMatchKit(seededFrom.match_kit),
    /** A tap on a row: calls the player up, or lets them go. The outcome is read from the state the
     *  row was drawn with, not from inside the updater, which React may run after this returns. */
    toggle(registrationId: number): ToggleOutcome {
      if (!isCalledUp(registrationId) && calledUp.length >= MAX_CALLED_UP) return 'called-up-full'
      setState((prev) => {
        if (prev.entries.some((entry) => entry.registrationId === registrationId)) {
          return { ...prev, entries: withoutEntry(prev.entries, registrationId) }
        }
        if (prev.entries.length >= MAX_CALLED_UP) return prev
        return { ...prev, entries: [...prev.entries, { registrationId, shirtNumber: null }] }
      })
      return 'ok'
    },
    setShirtNumber(registrationId: number, shirtNumber: number | null) {
      setState((prev) => ({
        ...prev,
        entries: prev.entries.map((entry) =>
          entry.registrationId === registrationId ? { ...entry, shirtNumber } : entry,
        ),
      }))
    },
    /** «Riprendi l'ultima convocazione»: replaces the whole call-up in one step, who and the
     *  numbers. The kit, the ritrovo and the notes are this match's, so they stay. */
    resumeFrom(entries: readonly CallUpEntry[]) {
      setState((prev) => ({ ...prev, entries }))
    },
    setMatchKit(matchKit: MatchKitDraft | null) {
      setState((prev) => ({ ...prev, matchKit }))
    },
    setMeetingTime(meetingTime: string) {
      setState((prev) => ({ ...prev, meetingTime }))
    },
    setMeetingPlace(meetingPlace: string) {
      setState((prev) => ({ ...prev, meetingPlace }))
    },
    setNotes(notes: string) {
      setState((prev) => ({ ...prev, notes }))
    },
  }
}

export function numberTaken(
  entries: readonly CallUpEntry[],
  registrationId: number,
  candidate: number,
): boolean {
  return entries.some(
    (entry) => entry.registrationId !== registrationId && entry.shirtNumber === candidate,
  )
}

/** A convocato wears a number that somebody else called up wears too: what the row, the board and the
 *  bar all say. No number is no conflict. */
export function hasNumberConflict(entries: readonly CallUpEntry[], entry: CallUpEntry): boolean {
  return entry.shirtNumber !== null && numberTaken(entries, entry.registrationId, entry.shirtNumber)
}

/** Somebody called up wears a number that somebody else called up wears too. */
export function hasSharedNumber(entries: readonly CallUpEntry[]): boolean {
  return entries.some((entry) => hasNumberConflict(entries, entry))
}

/** The name of a convocato's number field, read and never written: «Numero di maglia di Cognome Nome». */
export function shirtNumberFieldLabel(player: LineupPerson): string {
  return interpolate(messages.lineup.shirtNumberField, { name: lineupPersonName(player) })
}

/** A meeting place is said beside a time (`team_sheet.meeting_place_without_time`); a place of blanks is no place. */
export function isPlaceWithoutTime(state: LineupDraftState): boolean {
  return orNull(state.meetingPlace) !== null && state.meetingTime === ''
}

/** A field left empty or blank is «non indicato»: sent as `null`, since the PUT replaces the
 *  convocazione whole and one left out is one taken away. */
function orNull(text: string): string | null {
  const trimmed = text.trim()
  return trimmed === '' ? null : trimmed
}

/** The ritrovo and the notes as the server holds them. Once the whistle has gone it refuses different ones
 *  (`team_sheet.details_locked`): a save sends these back as they stand. */
export type HeldMeeting = Pick<TeamSheetRead, 'meeting_at' | 'meeting_place' | 'notes'>

export function heldMeetingOf(sheet: HeldMeeting): HeldMeeting {
  return { meeting_at: sheet.meeting_at, meeting_place: sheet.meeting_place, notes: sheet.notes }
}

function typedMeeting(draft: LineupDraftState, kicksOffAt: string): HeldMeeting {
  return {
    meeting_at: draft.meetingTime === '' ? null : meetingInstant(kicksOffAt, draft.meetingTime),
    meeting_place: orNull(draft.meetingPlace),
    notes: orNull(draft.notes),
  }
}

/** Who is called up and their shirt, if any; kit, ritrovo and notes go with every save, like the
 *  rest. The ritrovo is the time typed on `kicksOffAt`'s day, unless `held` says the whistle has gone:
 *  then it is what the server holds, whichever day the match was moved to since. */
export function draftToBody(
  draft: LineupDraftState,
  kicksOffAt: string,
  held?: HeldMeeting,
): TeamSheetPut {
  return {
    call_ups: draft.entries.map((entry) => ({
      player_registration_id: entry.registrationId,
      shirt_number: entry.shirtNumber,
    })),
    match_kit: toMatchKitPut(draft.matchKit),
    ...(held ?? typedMeeting(draft, kicksOffAt)),
  }
}

export function useSaveTeamSheet(matchId: number, enrollmentId: number) {
  const cache = useQueryClient()
  return {
    save: (body: TeamSheetPut) => putTeamSheet(api, matchId, enrollmentId, body),
    onSaved: (updated: TeamSheetRead) => {
      cache.setQueryData(teamSheetKey(matchId, enrollmentId), updated)
    },
  }
}

export function usePublishTeamSheet(matchId: number, enrollmentId: number) {
  const cache = useQueryClient()
  return {
    // Publishing reads the stored convocazione, so what is only in the browser has to be written
    // first, or the server publishes what it holds and not what the page shows.
    publish: async (body: TeamSheetPut) => {
      await putTeamSheet(api, matchId, enrollmentId, body)
      return publishTeamSheet(api, matchId, enrollmentId)
    },
    onPublished: (updated: TeamSheetRead) => {
      cache.setQueryData(teamSheetKey(matchId, enrollmentId), updated)
    },
  }
}

function mostRecentPast(matches: readonly MatchRead[], excludingMatchId: number): MatchRead | null {
  const past = matches
    .filter((match) => match.id !== excludingMatchId && match.status === 'played')
    .sort((a, b) => b.kicks_off_at.localeCompare(a.kicks_off_at))
  return past[0] ?? null
}

/** «Riprendi l'ultima convocazione»: the team's own previous matchday sheet, who and the shirt
 *  numbers — `undefined` while there is none to offer, so the button stays hidden rather than
 *  resuming an empty draft. */
export function usePreviousCallUp(
  teamId: number,
  currentMatchId: number,
): readonly CallUpEntry[] | undefined {
  const matches = useQuery(teamMatchesQuery(teamId))
  const previousMatch =
    matches.data === undefined ? null : mostRecentPast(matches.data, currentMatchId)

  const previousSide = (match: MatchRead): MatchSide =>
    match.home.team_id === teamId ? match.home : match.away

  const sheet = useQuery({
    ...teamSheetQuery(
      previousMatch?.id ?? 0,
      previousMatch === null ? 0 : previousSide(previousMatch).enrollment_id,
    ),
    enabled: previousMatch !== null,
  })

  if (previousMatch === null || sheet.data === undefined || sheet.data.call_ups.length === 0) {
    return undefined
  }
  return sheet.data.call_ups.map(toCallUpEntry)
}
